import { useEffect } from 'react';

/**
 * Fires the browser's native "leave site?" prompt while `isDirty` is true.
 *
 * This covers closing the tab, reloading, and typing a new URL. It does *not*
 * cover navigating away inside the app: blocking that needs react-router's
 * `useBlocker`, which only exists on a data router, and cloud-ui mounts a plain
 * `BrowserRouter` (features/scaffolding/components/App.tsx). In-app navigation
 * away from the editor therefore discards the draft silently — switching between
 * configs on the page itself is guarded separately, in the page.
 */
export const useUnsavedChangesWarning = (isDirty: boolean) => {
  useEffect(() => {
    if (!isDirty) return undefined;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Assigning returnValue is what actually triggers the prompt in Chrome;
      // the text is ignored by every current browser.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
};

export default useUnsavedChangesWarning;
